import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Answer } from '../../types/answer';
import { ChecklistReviewRow, ReviewAnswer } from '../../types/reviewAnswer';
import { DataTableFilterMeta } from 'primereact/datatable';
import { defaultReviewFilters } from '../../utils/reviewFilters';
import { TablePageState, TableSortState } from '../../types/table';
import { Inspection } from '../../types/inspection';
import { Asset } from '../../types/asset';

export interface AnswerState {
    inspections: Inspection[];
    loadingInspections: boolean;
    errorInspections: boolean;
    selectedInspection: Inspection | undefined;
    surveyorAnswers: Answer[];
    surveyorLoading: boolean;
    surveyorError: boolean;
    reviewAnswers: ReviewAnswer[];
    // dataDictionaryIds whose last save failed — the row stays optimistically
    // edited but is flagged so the reviewer knows it isn't persisted.
    failedEdits: string[];
    filteredReviewRows: ChecklistReviewRow[];
    // Session cache of resolved question-image assets, keyed by assetId. Holds
    // only the presigned URL/metadata (never the in-memory objectURL blob), so
    // re-opening a gallery skips the asset-manager round-trip. Cleared when the
    // selected inspection changes. Presigned URLs have a 7-day expiry, well
    // beyond a review session.
    resolvedAssets: Record<string, Asset>;
    loading: boolean;
    error: boolean;
    globalFilterValue: string;
    filters: DataTableFilterMeta;
    sort: TableSortState;
    page: TablePageState;
}

export const initialState: AnswerState = {
    inspections: [],
    loadingInspections: true,
    errorInspections: false,
    selectedInspection: undefined,
    surveyorAnswers: [],
    surveyorLoading: false,
    surveyorError: false,
    reviewAnswers: [],
    failedEdits: [],
    filteredReviewRows: [],
    resolvedAssets: {},
    loading: false,
    error: false,
    globalFilterValue: "",
    filters: defaultReviewFilters,
    sort: { sortField: undefined, sortOrder: null },
    page: { row: 50, first: 0 },
};

const sectionFilterValue = (filters: DataTableFilterMeta): unknown => {
    const meta = filters.sectionTitle;
    return meta && typeof meta === 'object' && 'value' in meta ? meta.value : null;
};

export const answerSlice = createSlice({
    name: 'answer',
    initialState,
    reducers: {
        setInspections: (state, action: PayloadAction<Inspection[]>) => {
            state.inspections = action.payload;
        },
        setLoadingInspections: (state, action: PayloadAction<boolean>) => {
            state.loadingInspections = action.payload;
        },
        setErrorInspections: (state, action: PayloadAction<boolean>) => {
            state.errorInspections = action.payload;
        },
        setSelectedInspection: (state, action: PayloadAction<Inspection | undefined>) => {
            // A different inspection's assets are unrelated, so drop the resolved-
            // URL cache when the selection actually changes (not on a same-
            // inspection refresh, which would needlessly re-resolve).
            if (action.payload?.id !== state.selectedInspection?.id) {
                state.resolvedAssets = {};
            }
            state.selectedInspection = action.payload;
        },
        // Merge freshly resolved question-image assets into the session cache,
        // keyed by assetId. Store the URL metadata only — the objectURL blob is
        // created per-open and revoked on close, so it must not be cached here.
        cacheResolvedAssets: (state, action: PayloadAction<Asset[]>) => {
            action.payload.forEach((asset) => {
                state.resolvedAssets[asset.asset_id] = { ...asset, objectURL: undefined };
            });
        },
        setSurveyorAnswers: (state, action: PayloadAction<Answer[]>) => {
            state.surveyorAnswers = action.payload;
        },
        setSurveyorLoading: (state, action: PayloadAction<boolean>) => {
            state.surveyorLoading = action.payload;
        },
        setSurveyorError: (state, action: PayloadAction<boolean>) => {
            state.surveyorError = action.payload;
        },
        setReviewAnswers: (state, action: PayloadAction<ReviewAnswer[]>) => {
            state.reviewAnswers = action.payload;
            // A fresh load is the backend truth — clear any stale failed flags.
            state.failedEdits = [];
        },
        // Optimistic upsert of a single edited review answer (keyed by
        // questionName). Recomputing selectChecklistReviewRows off this is what
        // makes an inline edit show immediately
        upsertReviewAnswer: (state, action: PayloadAction<ReviewAnswer>) => {
            const incoming = action.payload;
            const index = state.reviewAnswers.findIndex(
                (answer) => answer.questionName === incoming.questionName
            );
            if (index >= 0) {
                state.reviewAnswers[index] = incoming;
            } else {
                state.reviewAnswers.push(incoming);
            }
        },
        // Drop review answers by questionName — the orphaned descendants hard-
        // deleted when a tech-reviewer edit hides their branch.
        removeReviewAnswers: (state, action: PayloadAction<string[]>) => {
            if (action.payload.length === 0) return;
            const remove = new Set(action.payload);
            state.reviewAnswers = state.reviewAnswers.filter(
                (answer) => !remove.has(answer.questionName)
            );
        },
        // Flag a question's edit as failed to persist (keyed by dataDictionaryId).
        // The optimistic value stays on screen; the row is marked so the reviewer
        // can re-save it. Cleared when a later save of the same question succeeds.
        markEditFailed: (state, action: PayloadAction<string>) => {
            if (!state.failedEdits.includes(action.payload)) {
                state.failedEdits.push(action.payload);
            }
        },
        clearEditFailed: (state, action: PayloadAction<string>) => {
            state.failedEdits = state.failedEdits.filter((id) => id !== action.payload);
        },
        setFilteredReviewRows: (state, action: PayloadAction<ChecklistReviewRow[]>) => {
            state.filteredReviewRows = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<boolean>) => {
            state.error = action.payload;
        },
        setGlobalFilterValue: (state, action: PayloadAction<string>) => {
            state.globalFilterValue = action.payload;
            // Sync with filters.global for DataTable filtering
            if (state.filters.global && typeof state.filters.global === 'object' && 'value' in state.filters.global) {
                state.filters.global.value = action.payload || null;
            }
        },
        setFilters: (state, action: PayloadAction<DataTableFilterMeta>) => {
            // Selecting "All sections" will reset the sort order if it was previously sorted by section
            // If any other is column is sorted, that will persist
            if (sectionFilterValue(state.filters) != null
                && sectionFilterValue(action.payload) == null
                && state.sort.sortField === 'sectionTitle') {
                state.sort = { sortField: undefined, sortOrder: null };
            }
            state.filters = action.payload;
        },
        setSort: (state, action: PayloadAction<TableSortState>) => {
            state.sort = action.payload;
        },
        // Filters reference values from the loaded checklist (the section
        // dropdown), so a filter carried over from another inspection can
        // silently hide every row — reset when the inspection changes.
        resetFilters: (state) => {
            state.filters = defaultReviewFilters;
            state.globalFilterValue = "";
            state.sort = { sortField: undefined, sortOrder: null };
            // Back to the first page, but keep the rows-per-page preference.
            state.page = { ...state.page, first: 0 };
        },
        setPage: (state, action: PayloadAction<TablePageState>) => {
            state.page = action.payload;
        },
    },
});

export const {
    setInspections,
    setLoadingInspections,
    setErrorInspections,
    setSelectedInspection,
    cacheResolvedAssets,
    setSurveyorAnswers,
    setSurveyorLoading,
    setSurveyorError,
    setReviewAnswers,
    upsertReviewAnswer,
    removeReviewAnswers,
    markEditFailed,
    clearEditFailed,
    setFilteredReviewRows,
    setLoading,
    setError,
    setGlobalFilterValue,
    setFilters,
    setSort,
    resetFilters,
    setPage,
} = answerSlice.actions;

export default answerSlice.reducer;
